import { create } from 'zustand'

import {
	clientAddTracksToProject,
	clientGetTracks,
	clientReorderTracksInProject,
} from '@/client'
import { addProject, getCurrentProjects } from '@/services/projectsService'

import { insertTracks } from './projectStore.utils'

interface ProjectState {
	projects: Project[]
	currentProject:
		| (Project & {
				currentTracks: Track[]
				similarTracks?: Track[]
		  })
		| null
	setCurrentProject: (project: Project, currentTracks: Track[]) => void
	setSimilarTracks: (similarTracks: Track[]) => void
	getProjects: () => void
	addProject: (name: string, description?: string) => Promise<Project>
	editProject: (project: Project) => void
	deleteProject: (id: string) => void
	duplicateProject: (project: Project) => void
	addTracksToProject: (
		projectId: string,
		trackIds: number[],
		position?: number
	) => Promise<void>
}

const useProjectStore = create<ProjectState>()((set, get) => ({
	projects: null,
	getProjects: async () => {
		const projects = await getCurrentProjects(1, 100, 0, 0)

		set({ projects })
	},
	currentProject: null,
	setCurrentProject: (project, tracks) => {
		set({
			currentProject: { ...project, currentTracks: tracks },
		})
	},
	setSimilarTracks: (similarTracks) => {
		set((state) => ({
			currentProject: { ...state.currentProject, similarTracks },
		}))
	},
	addProject: async (name, description) => {
		const project = await addProject(name, description)

		if (project) {
			set((state) => ({
				projects: [project, ...(state.projects ?? [])],
			}))

			return project
		}

		return null
	},
	editProject: (project) => {
		set((state) => ({
			projects: state.projects?.map((x) => (x.Id === project.Id ? project : x)),
		}))
	},
	deleteProject: (id) => {
		set((state) => ({
			projects: state.projects?.filter((project) => project.Id !== id),
		}))
	},
	duplicateProject: (project) => {
		set((state) => ({
			projects: [project, ...(state.projects ?? [])],
		}))
	},
	addTracksToProject: async (projectId, trackIds, position?: number) => {
		const tracksWithNotes = await clientAddTracksToProject(projectId, trackIds)

		// checks what tracks we already have locally
		const similarTracks = get().currentProject?.similarTracks ?? []
		const similarTrackMap = new Map(
			similarTracks.map((track) => [track.id, track])
		)

		// which requested tracks are already in the store
		const tracksFromStore = trackIds
			.filter((id) => similarTrackMap.has(id))
			.map((id) => similarTrackMap.get(id))

		// which tracks do we need to fetch that aren't in the store
		const remainingTrackIds = trackIds.filter((id) => !similarTrackMap.has(id))

		// tracks returned from the api that weren't already in the store
		const tracksFromApi =
			remainingTrackIds.length > 0
				? await clientGetTracks(remainingTrackIds)
				: []

		const newTracks = [...tracksFromStore, ...tracksFromApi]

		set((state) => {
			if (projectId !== state.currentProject?.Id) return {}

			return {
				currentProject: {
					...state.currentProject,
					Tracks: [...(state.currentProject?.Tracks || []), ...tracksWithNotes],
					currentTracks: insertTracks(
						state.currentProject?.currentTracks ?? [],
						newTracks,
						position
					),
					similarTracks:
						state.currentProject.similarTracks?.filter(
							(track) => !trackIds.includes(track.id)
						) ?? [],
				},
			}
		})
		if (position !== undefined) {
			const updatedTracks = get().currentProject?.currentTracks ?? []
			await clientReorderTracksInProject(
				projectId,
				updatedTracks.map((track) => track.id)
			)
		}
	},
}))

export default useProjectStore
